import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { zip } from "fflate";
import { getAccessTokenClaims } from "@packages/api";
import type {
  PDFImage,
  Participant,
  CallDetails,
  CallLinkDetails,
  SessionDetails,
  TranscriptDetails,
  TranscriptSegment,
  InstructionSet as PdfInstructionSet,
} from "@packages/pdf";
import { downloadFileContent } from "../api/useResourceUrl";
import type {
  Call,
  CallContentDto,
  ContentResource,
  TranscriptSession,
  InstructionSet,
} from "../types";
import { idFromUri, schemeFromUri, sharableLinkDownloadUrl } from "../utils";

/** @internal Exported for testing */
export const timezoneOffsetAsString = (offset: number) => {
  const hours = offset / -60;
  const isPositive = hours >= 0;
  return `UTC${isPositive ? "+" : "-"}${Math.abs(hours).toString().padStart(2, "0")}:00`;
};

/** @internal Exported for testing */
export function buildCallDetails(
  startedAt: string | null,
  endedAt: string | null,
  meeting: Call["meeting"] | undefined,
): { callDetails: CallDetails; sessionDetails: SessionDetails | undefined } {
  let sessionDetails: SessionDetails | undefined;
  let startIso: string | null;
  let endIso: string | null;

  if (meeting) {
    sessionDetails = {
      title: meeting.title,
      description: meeting.description ?? "",
    };
    startIso = meeting.startedAt;
    endIso = meeting.endedAt;
  } else {
    startIso = startedAt;
    endIso = endedAt;
  }

  const startDate = new Date(startIso || endIso || Date.now());
  const endDate = endIso ? new Date(endIso) : new Date();

  const diffMs = endDate.getTime() - startDate.getTime();
  const totalSeconds = Math.floor(diffMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  let duration: string;
  if (hours > 0) {
    duration = `${hours}h ${minutes}m ${seconds}s`;
  } else if (minutes > 0) {
    duration = `${minutes}m ${seconds}s`;
  } else {
    duration = `${seconds}s`;
  }

  const timezoneOffset = startDate.getTimezoneOffset();
  const timezoneName = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const timezoneStr = `${timezoneName} ${timezoneOffsetAsString(timezoneOffset)}`;

  const adjustedDate = new Date(startDate);
  adjustedDate.setMinutes(adjustedDate.getMinutes() - timezoneOffset);
  const [dateStr, clockPart] = adjustedDate.toISOString().split("T");
  const startTimeStr = `${clockPart.substring(0, 5)} (${timezoneStr})`;

  return {
    callDetails: {
      startTime: startTimeStr,
      date: dateStr,
      duration,
    },
    sessionDetails,
  };
}

/** @internal Exported for testing */
export function buildParticipants(
  participants: Call["participants"],
  organization: string,
): Participant[] {
  return participants.map((p) => ({
    name: p.user?.name ?? "Unknown",
    organization,
    callLinkUser: !p.user?.email,
  }));
}

/** @internal Exported for testing */
export function buildCallLinkDetails(
  callLink: Call["callLink"],
): CallLinkDetails | undefined {
  if (!callLink) return undefined;
  return {
    companyName: callLink.companyName,
    reference: callLink.reference,
    topic: callLink.topic,
  };
}

async function loadImagesFromContent(
  content: CallContentDto | undefined,
  domainId: number,
): Promise<PDFImage[]> {
  if (!content?.resources) return [];

  const imageResources = content.resources.filter(
    (r: ContentResource) => r.type === "image",
  );

  const results = await Promise.all(
    imageResources.map(async (resource) => {
      try {
        const scheme = schemeFromUri(resource.uri);
        let blob: Blob;
        if (scheme.startsWith('xm')) {
          const fileId = idFromUri(resource.uri);
          blob = await downloadFileContent(domainId, fileId);
        } else if (resource.sharableLink) {
          const res = await fetch(sharableLinkDownloadUrl(resource.sharableLink));
          if (!res.ok) return null;
          blob = await res.blob();
        } else {
          return null;
        }
        const imageElement = new Image();
        imageElement.src = URL.createObjectURL(blob);
        await imageElement.decode();
        return { id: resource.id, image: imageElement };
      } catch {
        return null;
      }
    }),
  );

  return results.filter((r): r is PDFImage => r !== null);
}

function downloadBlob(blob: Blob, prefix: string, extension = "pdf") {
  const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
  const filename = `xmreality-${prefix}-${timestamp}.${extension}`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** @internal Exported for testing */
export function extensionFromMime(mimeType: string): string {
  const map: Record<string, string> = {
    "image/jpeg": "jpg",
    "image/png": "png",
    "image/gif": "gif",
    "image/webp": "webp",
    "image/bmp": "bmp",
    "image/svg+xml": "svg",
  };
  return map[mimeType] ?? "jpg";
}

export function useExportPdf(params: {
  callId: number;
  call: Call;
  content: CallContentDto | undefined;
  instructionSet: InstructionSet | undefined;
  transcriptSession: TranscriptSession | undefined;
  domainId: number;
}) {
  const { call, content, instructionSet, transcriptSession, domainId } = params;
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const { i18n } = useTranslation();

  const tFunction = useCallback(
    (key: string) => i18n.t(`pdf.${key}`),
    [i18n],
  );

  const exportCallReport = useCallback(async () => {
    setExportError(null);
    setIsExporting(true);
    let images: PDFImage[] = [];
    try {
      const claims = getAccessTokenClaims();
      const organization = claims?.xm_domain_name ?? "";

      const { callDetails, sessionDetails } = buildCallDetails(
        call.startedAt,
        call.endedAt,
        call.meeting,
      );
      const participants = buildParticipants(call.participants, organization);
      const callLinkDetails = buildCallLinkDetails(call.callLink);

      images = await loadImagesFromContent(content, domainId);

      const analysis = transcriptSession?.transcriptAnalyses?.[0];
      const transcriptDetails: TranscriptDetails | undefined =
        analysis?.summary
          ? {
              summary: analysis.summary,
              topics: analysis.topics,
            }
          : undefined;

      const callNotes =
        content?.comments?.find((c) => c.stepId === null)?.comment ?? "";

      const appendixImageIds: number[] = [];
      if (content?.resources) {
        for (const r of content.resources) {
          if (r.type === "image" && r.stepIds.some((s) => s === null)) {
            appendixImageIds.push(r.id);
          }
        }
      }

      // Build step-to-resource mapping from call content (not instruction set template)
      const stepImageIds: Record<string, number[]> = {};
      if (content?.resources) {
        for (const r of content.resources) {
          if (r.type === "image") {
            for (const stepId of r.stepIds) {
              if (stepId != null) {
                const key = String(stepId);
                if (!stepImageIds[key]) stepImageIds[key] = [];
                stepImageIds[key].push(r.id);
              }
            }
          }
        }
      }

      let pdfInstructionSet: PdfInstructionSet | undefined;
      if (instructionSet?.steps) {
        pdfInstructionSet = {
          name: instructionSet.name,
          description: "",
          steps: instructionSet.steps.map((step) => ({
            id: step.id,
            mainText: step.mainText,
            secondaryText: step.secondaryText,
            comment: step.comment,
            actions: step.actions?.map((a) => ({
              checked: !!a.checked,
              text: a.text,
            })),
            imageIds: stepImageIds[step.id] ?? [],
          })),
        };
      }

      const { createCallReportPdf } = await import("@packages/pdf");
      const blob = await createCallReportPdf(
        {
          primaryColor: "#0D64A5",
          secondaryColor: "#fff",
          languageCode: i18n.resolvedLanguage ?? "en",
          tFunction,
        },
        {
          callDetails,
          sessionDetails,
          callLinkDetails,
          participants,
          transcriptDetails,
          callNotes,
          images,
          appendixImageIds,
          instructionSet: pdfInstructionSet,
        },
      );

      downloadBlob(blob, "report");
    } catch (error) {
      console.error("Error generating Call Report PDF:", error);
      setExportError("callReport");
    } finally {
      images.forEach((img) => URL.revokeObjectURL(img.image.src));
      setIsExporting(false);
    }
  }, [call, content, instructionSet, transcriptSession, domainId, i18n, tFunction]);

  const exportTranscript = useCallback(async () => {
    setExportError(null);
    setIsExporting(true);
    try {
      const claims = getAccessTokenClaims();
      const organization = claims?.xm_domain_name ?? "";

      const { callDetails, sessionDetails } = buildCallDetails(
        call.startedAt,
        call.endedAt,
        call.meeting,
      );
      const participants = buildParticipants(call.participants, organization);
      const callLinkDetails = buildCallLinkDetails(call.callLink);

      const transcript = transcriptSession?.transcripts?.[0];
      const transcriptSegments: TranscriptSegment[] = (
        transcript?.segments ?? []
      )
        .filter((segment) => segment.source === "user")
        .map((segment) => {
          const participant = call.participants.find(
            (p) => p.user?.id === segment.userId,
          );
          const name = participant?.user?.name ?? "Unknown";
          return {
            name,
            text: segment.text,
            dateTime: new Date(segment.timestamp),
          };
        });

      const { createTranscriptPdf } = await import("@packages/pdf");
      const blob = await createTranscriptPdf(
        {
          primaryColor: "#0D64A5",
          secondaryColor: "#fff",
          languageCode: i18n.resolvedLanguage ?? "en",
          tFunction,
        },
        {
          callDetails,
          sessionDetails,
          callLinkDetails,
          participants,
          transcriptSegments,
        },
      );

      downloadBlob(blob, "transcript");
    } catch (error) {
      console.error("Error generating Transcript PDF:", error);
      setExportError("transcript");
    } finally {
      setIsExporting(false);
    }
  }, [call, transcriptSession, domainId, i18n, tFunction]);

  const exportImages = useCallback(async () => {
    setExportError(null);
    setIsExporting(true);
    try {
      const imageResources = (content?.resources ?? []).filter(
        (r: ContentResource) => r.type === "image",
      );

      const entries = (await Promise.all(
        imageResources.map(async (resource, index) => {
          try {
            const scheme = schemeFromUri(resource.uri);
            let blob: Blob;
            if (scheme.startsWith('xm')) {
              const fileId = idFromUri(resource.uri);
              blob = await downloadFileContent(domainId, fileId);
            } else if (resource.sharableLink) {
              const res = await fetch(sharableLinkDownloadUrl(resource.sharableLink));
              if (!res.ok) return null;
              blob = await res.blob();
            } else {
              return null;
            }
            const ext = extensionFromMime(resource.mimeType);
            const name = `image-${String(index + 1).padStart(3, "0")}.${ext}`;
            const buffer = new Uint8Array(await blob.arrayBuffer());
            return [name, buffer] as const;
          } catch {
            return null;
          }
        }),
      )).filter((e) => e !== null);

      const files: Record<string, Uint8Array> = {};
      for (const [name, data] of entries) {
        files[name] = data;
      }

      const zipped = await new Promise<Uint8Array>((resolve, reject) => {
        zip(files, (err, data) => (err ? reject(err) : resolve(data)));
      });
      const zipBlob = new Blob([zipped.buffer as ArrayBuffer], { type: "application/zip" });
      downloadBlob(zipBlob, "images", "zip");
    } catch (error) {
      console.error("Error exporting images:", error);
      setExportError("images");
    } finally {
      setIsExporting(false);
    }
  }, [content, domainId]);

  return { exportCallReport, exportTranscript, exportImages, isExporting, exportError, clearExportError: () => setExportError(null) };
}
